// src/components/pages/client/ExploreCompanies.tsx

import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Input } from '@/components/ui/input';
import {
  Building,
  Search,
  Star,
  MapPin,
  Phone,
  Calendar,
  Briefcase,
  Sparkles,
  Heart,
} from 'lucide-react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { GLASS_CARD, GLASS_SURFACE } from '@/components/ui/glass';
import { useEmpresasExplore } from '@/components/hooks/useEmpresasExplore';
import LocationDialog from '@/components/pages/shared/LocationDialog';
import Pagination from '@/components/pages/shared/Pagination';
import FiltrosEmpresas, {
  FILTROS_VACIOS,
  RADIOS_KM,
  aplicarFiltros,
  subcategoriasDe,
  type FiltrosEmpresasValor,
  type OpcionSlug,
} from '@/components/pages/shared/FiltrosEmpresas';

const DEFAULT_PAGE_SIZE = 6;
// Grid de 3 columnas en desktop: las opciones deben ser múltiplos de 3 para
// que la última fila no quede coja.
const PAGE_SIZE_OPTIONS = [3, 6, 9, 12, 24];
const PAGE_SIZE_STORAGE_KEY = 'bookoll:explore-companies:pageSize';

const readStoredPageSize = () => {
  if (typeof window === 'undefined') return DEFAULT_PAGE_SIZE;
  const stored = Number(localStorage.getItem(PAGE_SIZE_STORAGE_KEY));
  return PAGE_SIZE_OPTIONS.includes(stored) ? stored : DEFAULT_PAGE_SIZE;
};
const SITE_URL = 'https://bookoll.com';
const canonicalUrl = `${SITE_URL}/explorar`;

const containerVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0, transition: { staggerChildren: 0.06 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 16 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.3 } },
};

const fadeVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.35, ease: 'easeOut' as const } },
};

// ── Schemas estáticos ─────────────────────────────────────────────────────────
const webPageSchema = {
  '@context': 'https://schema.org',
  '@type': 'CollectionPage',
  '@id': `${canonicalUrl}#webpage`,
  name: 'Explorar negocios — Bookoll',
  description:
    'Explora todos los negocios de servicios disponibles en Bookoll. Peluquerías, clínicas, entrenadores, estética y mucho más. Reserva cita online en segundos.',
  url: canonicalUrl,
  inLanguage: 'es-ES',
  isPartOf: {
    '@type': 'WebSite',
    '@id': `${SITE_URL}/#website`,
    name: 'Bookoll',
    url: SITE_URL,
  },
};

const breadcrumbSchema = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'Inicio', item: SITE_URL },
    {
      '@type': 'ListItem',
      position: 2,
      name: 'Explorar negocios',
      item: canonicalUrl,
    },
  ],
};

// ── Helpers ───────────────────────────────────────────────────────────────────
const getCategoryIcon = (categoria?: string) => {
  if (categoria?.toLowerCase().includes('belleza'))
    return <Sparkles className="w-3 h-3" />;
  if (categoria?.toLowerCase().includes('salud'))
    return <Heart className="w-3 h-3" />;
  return <Briefcase className="w-3 h-3" />;
};

const MAX_VISIBLE_SERVICES = 3;

// ── Skeleton de tarjeta (misma silueta que la card real) ────────────────────
const shimmer =
  'relative overflow-hidden before:absolute before:inset-0 before:-translate-x-full before:animate-[shimmer_1.6s_infinite] before:bg-gradient-to-r before:from-transparent before:via-white/40 dark:before:via-white/10 before:to-transparent';
const bone = 'rounded-md bg-gray-200/70 dark:bg-gray-700/50';

const CompanyCardSkeleton = () => (
  <div className={`flex flex-col w-full rounded-2xl ${GLASS_CARD} overflow-hidden`}>
    <div className={`w-full h-48 shrink-0 ${bone} ${shimmer}`} />
    <div className="flex flex-col flex-1 p-4 space-y-2.5">
      <div className={`h-4 w-3/4 ${bone} ${shimmer}`} />
      <div className={`h-5 w-24 rounded-full ${bone} ${shimmer}`} />
      <div className={`h-3.5 w-28 ${bone} ${shimmer}`} />
      <div className="flex gap-1.5 h-[44px]">
        <div className={`h-5 w-14 rounded-full ${bone} ${shimmer}`} />
        <div className={`h-5 w-16 rounded-full ${bone} ${shimmer}`} />
      </div>
      <div className="flex gap-2 pt-1 mt-auto">
        <div className={`h-8 flex-1 rounded-xl ${bone} ${shimmer}`} />
        <div className={`h-8 flex-1 rounded-xl ${bone} ${shimmer}`} />
      </div>
    </div>
  </div>
);

const ExploreCompaniesSkeleton = () => (
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
    {Array.from({ length: 6 }).map((_, i) => (
      <CompanyCardSkeleton key={i} />
    ))}
  </div>
);
// ─────────────────────────────────────────────────────────────────────────

// ── Componente principal ──────────────────────────────────────────────────────
const ExploreCompanies = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(readStoredPageSize);

  const {
    empresasUsuario: empresas,
    loadingEmpresas,
    errorEmpresas,
    userLocation,
    showLocationDialog,
    solicitarUbicacion,
    handleAllowLocation,
    handleRejectLocation,
  } = useEmpresasExplore();

  // Filtros del panel (categoría, subcategoría, valoradas, cercanía, online)
  const [filtros, setFiltros] = useState<FiltrosEmpresasValor>(FILTROS_VACIOS);

  // "Cerca de mí" sin ubicación: se pide y el filtro se aplica al llegar.
  const [esperandoUbicacion, setEsperandoUbicacion] = useState(false);

  useEffect(() => {
    if (esperandoUbicacion && userLocation) {
      setFiltros((f) => ({ ...f, radioKm: RADIOS_KM[1] }));
      setEsperandoUbicacion(false);
    }
  }, [esperandoUbicacion, userLocation]);

  const pedirUbicacion = () => {
    setEsperandoUbicacion(true);
    solicitarUbicacion();
  };

  const categorias = useMemo<OpcionSlug[]>(() => {
    const mapa = new Map<string, string>();
    empresas.forEach((e) => {
      if (e.categoria_slug && e.categoria && !mapa.has(e.categoria_slug)) {
        mapa.set(e.categoria_slug, e.categoria);
      }
    });
    return Array.from(mapa, ([slug, nombre]) => ({ slug, nombre })).sort((a, b) =>
      a.nombre.localeCompare(b.nombre)
    );
  }, [empresas]);

  const subcategorias = useMemo(
    () => subcategoriasDe(empresas, filtros.categoria),
    [empresas, filtros.categoria]
  );

  // El orden base (reservadas > valoración) ya viene del hook; los filtros
  // solo recortan la lista y lo respetan.
  const conFiltros = useMemo(() => aplicarFiltros(empresas, filtros), [empresas, filtros]);

  const filtered = searchTerm.trim()
    ? conFiltros.filter((e) => {
        const q = searchTerm.toLowerCase();
        const matchNombre = e.empresa?.toLowerCase().includes(q);
        const matchServicio = e.servicios?.some((s: string) =>
          s.toLowerCase().includes(q)
        );
        const matchSubcategoria = e.subcategorias?.some((s: string) =>
          s.toLowerCase().includes(q)
        );
        return matchNombre || matchServicio || matchSubcategoria;
      })
    : conFiltros;

  const totalPages = Math.ceil(filtered.length / pageSize);
  const currentData = filtered.slice((page - 1) * pageSize, page * pageSize);

  useEffect(() => {
    setPage(1);
  }, [searchTerm, filtros]);

  useEffect(() => {
    setPage(1);
    if (typeof window !== 'undefined') {
      localStorage.setItem(PAGE_SIZE_STORAGE_KEY, String(pageSize));
    }
  }, [pageSize]);

  const renderStars = (
    valoracion: number | null | undefined,
    totalValoraciones: number | null | undefined
  ) => {
    if (!valoracion || valoracion <= 0 || !totalValoraciones) {
      return <div className="h-[18px]" />;
    }

    const rounded = Math.ceil(Number(valoracion));
    return (
      <div className="flex items-center gap-0.5">
        {Array.from({ length: 5 }).map((_, i) => (
          <Star
            key={i}
            className={`w-3 h-3 ${
              i < rounded
                ? 'text-yellow-400 fill-yellow-400'
                : 'text-gray-200 fill-gray-200 dark:text-gray-700 dark:fill-gray-700'
            }`}
          />
        ))}
        <span className="text-xs font-semibold text-gray-700 dark:text-gray-300 ml-0.5">
          {valoracion.toFixed(1)}
        </span>
        <span className="text-[10px] text-gray-400 dark:text-gray-500">
          ({totalValoraciones})
        </span>
      </div>
    );
  };

  if (errorEmpresas) {
    return (
      <div className="text-center text-red-500 mt-10 text-sm">
        Error al cargar empresas: {errorEmpresas}
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Explorar negocios — Reserva cita online | Bookoll</title>
        <meta
          name="description"
          content="Explora todos los negocios disponibles en Bookoll. Peluquerías, clínicas, entrenadores personales, centros de estética y más. Reserva cita online 24h."
        />
        <meta
          name="keywords"
          content="explorar negocios, reserva cita online, peluquerías cerca, clínicas online, entrenador personal, estética reserva, negocios servicios España, Bookoll"
        />
        <link rel="canonical" href={canonicalUrl} />
        <meta
          name="robots"
          content="index, follow, max-snippet:-1, max-image-preview:large"
        />
        <link rel="alternate" hrefLang="es-ES" href={canonicalUrl} />
        <link rel="alternate" hrefLang="x-default" href={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Bookoll" />
        <meta property="og:locale" content="es_ES" />
        <meta
          property="og:title"
          content="Explorar negocios — Reserva cita online | Bookoll"
        />
        <meta
          property="og:description"
          content="Explora todos los negocios disponibles en Bookoll y reserva cita online en segundos."
        />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content={`${SITE_URL}/og-home.jpg`} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="Explorar negocios en Bookoll" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@bookoll" />
        <meta name="twitter:title" content="Explorar negocios — Bookoll" />
        <meta
          name="twitter:description"
          content="Explora negocios y reserva cita online en segundos con Bookoll."
        />
        <meta name="twitter:image" content={`${SITE_URL}/og-home.jpg`} />
        <script type="application/ld+json">
          {JSON.stringify(webPageSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      </Helmet>

      <motion.div
        className="w-full space-y-4 pb-28 lg:pb-0"
        initial="hidden"
        animate="visible"
        variants={containerVariants}
      >
        {/* Cabecera */}
        <motion.div
          variants={itemVariants}
          className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400"
        >
          <span className="text-2xl font-bold tracking-tight text-gray-900 dark:text-white">
            Explorar empresas
          </span>
        </motion.div>

        {/* Buscador + contador — tarjeta igual que CompanyProfile */}
        <Card className={`rounded-2xl ${GLASS_CARD}`}>
          <CardContent className="p-4 space-y-3">
            <div className="relative w-full">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 z-10" />
              <Input
                type="text"
                placeholder="Buscar por nombre, servicio o categoría..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 rounded-xl text-sm w-full border-gray-300/70 dark:border-white/10 bg-white/70 dark:bg-black/40 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:border-[#103254] focus:ring-1 focus:ring-[#103254]"
              />
            </div>
            <div className="flex items-center justify-between gap-3">
              <FiltrosEmpresas
                valor={filtros}
                onChange={setFiltros}
                categorias={categorias}
                subcategorias={subcategorias}
                tieneUbicacion={!!userLocation}
                onPedirUbicacion={pedirUbicacion}
              />
              {!loadingEmpresas && filtered.length > 0 && (
                <p className="text-xs text-gray-400 dark:text-gray-500">
                  {filtered.length} empresa{filtered.length !== 1 ? 's' : ''}{' '}
                  encontrada{filtered.length !== 1 ? 's' : ''}
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Lista */}
        {loadingEmpresas ? (
          <motion.div
            variants={fadeVariants}
            initial="hidden"
            animate="visible"
          >
            <ExploreCompaniesSkeleton />
          </motion.div>
        ) : currentData.length === 0 ? (
          <motion.div
            variants={itemVariants}
            className={`rounded-2xl ${GLASS_CARD} text-center py-16 text-gray-400 dark:text-gray-600`}
          >
            <Building className="w-10 h-10 mx-auto mb-3 text-gray-200 dark:text-gray-700" />
            <p className="text-sm">No se encontraron empresas.</p>
            {(searchTerm || filtros !== FILTROS_VACIOS) && (
              <button
                type="button"
                onClick={() => {
                  setSearchTerm('');
                  setFiltros(FILTROS_VACIOS);
                }}
                className="mt-4 text-xs text-[#103254] dark:text-blue-400 underline underline-offset-2"
              >
                Quitar filtros y búsqueda
              </button>
            )}
          </motion.div>
        ) : (
          <>
            <motion.div
              variants={containerVariants}
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
            >
              {currentData.map((empresa) => {
                const visibleServices =
                  empresa.servicios?.slice(0, MAX_VISIBLE_SERVICES) ?? [];
                const extraCount =
                  (empresa.servicios?.length ?? 0) - MAX_VISIBLE_SERVICES;

                return (
                  <motion.div
                    key={empresa.id_empresa}
                    variants={itemVariants}
                    className="flex"
                  >
                    <div
                      className={`group flex flex-col w-full rounded-2xl ${GLASS_CARD} hover:shadow-lg hover:border-[#103254]/20 dark:hover:border-blue-400/20 transition-all duration-300 overflow-hidden`}
                    >
                      {/* Imagen de cabecera */}
                      <div className="w-full h-48 shrink-0 overflow-hidden bg-gray-100 dark:bg-gray-800/60 relative">
                        {empresa.imagenes?.length ? (
                          <img
                            src={empresa.imagenes[0]}
                            alt={`${empresa.empresa} — reserva cita online`}
                            loading="lazy"
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                        ) : empresa.logo ? (
                          <img
                            src={empresa.logo}
                            alt={`Logo de ${empresa.empresa}`}
                            loading="lazy"
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Building className="w-10 h-10 text-gray-300 dark:text-gray-600" />
                          </div>
                        )}
                        {/* Badge sobre la imagen */}
                        {empresa.reservado_previamente && (
                          <span
                            className={`absolute top-3 right-3 text-[10px] font-semibold text-[#103254] dark:text-blue-400 ${GLASS_SURFACE} px-2 py-0.5 rounded-full shadow-sm border border-white/40 dark:border-white/10`}
                          >
                            {Number(empresa.total_reservas_usuario ?? 0) > 1
                              ? `Reservado ${empresa.total_reservas_usuario} veces`
                              : 'Reservado antes'}
                          </span>
                        )}
                      </div>

                      {/* Contenido */}
                      <div className="flex flex-col flex-1 p-4 space-y-2.5">
                        {/* Nombre */}
                        <h2 className="font-semibold text-gray-900 dark:text-white text-sm leading-tight">
                          {empresa.empresa}
                        </h2>

                        {/* Categoría chip */}
                        <div className="flex items-center gap-1.5 text-[10px] text-gray-500 dark:text-gray-400 bg-white/40 dark:bg-white/5 border border-white/40 dark:border-white/10 rounded-full px-2.5 py-1 w-fit">
                          {getCategoryIcon(empresa.categoria)}
                          <span>{empresa.categoria}</span>
                        </div>

                        {/* Estrellas */}
                        {renderStars(
                          empresa.valoracion_media_empresa,
                          empresa.total_valoraciones
                        )}

                        {/* Meta: teléfono, ciudad, distancia */}
                        <div className="flex flex-wrap gap-x-3 gap-y-1">
                          {empresa.telefono && (
                            <div className="flex items-center gap-1 text-xs text-gray-400 dark:text-gray-500">
                              <Phone className="w-3 h-3 shrink-0" />
                              <span>{empresa.telefono}</span>
                            </div>
                          )}
                          {empresa.ciudad && (
                            <div className="flex items-center gap-1 text-xs text-gray-400 dark:text-gray-500">
                              <MapPin className="w-3 h-3 shrink-0" />
                              <span>{empresa.ciudad}</span>
                            </div>
                          )}
                          {empresa.distancia != null && (
                            <div className="flex items-center gap-1 text-xs text-gray-400 dark:text-gray-500">
                              <MapPin className="w-3 h-3 shrink-0" />
                              <span>{empresa.distancia.toFixed(1)} km</span>
                            </div>
                          )}
                        </div>

                        {/* Pills de servicios */}
                        {visibleServices.length > 0 && (
                          <div className="flex flex-wrap gap-1.5 h-[44px] overflow-hidden content-start">
                            {visibleServices.map((s: string) => (
                              <span
                                key={s}
                                className="text-[10px] text-gray-500 dark:text-gray-400 bg-white/40 dark:bg-white/5 border border-white/40 dark:border-white/10 rounded-full px-2 py-0.5 h-fit"
                              >
                                {s}
                              </span>
                            ))}
                            {extraCount > 0 && (
                              <span className="text-[10px] text-gray-400 dark:text-gray-500 bg-white/40 dark:bg-white/5 border border-white/40 dark:border-white/10 rounded-full px-2 py-0.5 h-fit">
                                +{extraCount} más
                              </span>
                            )}
                          </div>
                        )}

                        {/* Acciones — pegadas al fondo */}
                        <div
                          className="flex gap-2 pt-1 mt-auto"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {empresa.reservas_online_citas &&
                          empresa.plan_incluye_reservas !== false ? (
                            <>
                              <Button
                                size="sm"
                                onClick={() =>
                                  navigate(
                                    `/client-booking/${empresa.id_empresa}`,
                                    { state: { showBackButton: true } }
                                  )
                                }
                                className="flex-1 text-white font-medium rounded-xl shadow-md shadow-[#103254]/20 hover:opacity-90 text-xs"
                                style={{ backgroundColor: '#103254' }}
                              >
                                <Calendar className="w-3.5 h-3.5 mr-1.5" />
                                Reservar
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() =>
                                  navigate(
                                    `/${empresa.categoria_slug}/${empresa.ciudad_slug}/${empresa.empresa_slug}`
                                  )
                                }
                                className={`flex-1 rounded-xl text-xs border-white/40 dark:border-white/10 ${GLASS_SURFACE} text-gray-600 dark:text-gray-300 hover:border-[#103254]/40 hover:text-[#103254] dark:hover:text-blue-400`}
                              >
                                Ver más
                              </Button>
                            </>
                          ) : (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() =>
                                navigate(
                                  `/${empresa.categoria_slug}/${empresa.ciudad_slug}/${empresa.empresa_slug}`
                                )
                              }
                              className={`w-full rounded-xl text-xs border-white/40 dark:border-white/10 ${GLASS_SURFACE} text-gray-600 dark:text-gray-300 hover:border-[#103254]/40 hover:text-[#103254] dark:hover:text-blue-400`}
                            >
                              Ver empresa
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </motion.div>

            {/* Paginación — componente compartido, igual que en el resto de listados */}
            <Pagination
              page={page}
              totalPages={totalPages}
              totalItems={filtered.length}
              pageSize={pageSize}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
              itemLabel="empresas"
              pageSizeOptions={PAGE_SIZE_OPTIONS}
            />
          </>
        )}
      </motion.div>

      <LocationDialog
        open={showLocationDialog}
        onAllow={handleAllowLocation}
        onReject={handleRejectLocation}
      />
    </>
  );
};

export default ExploreCompanies;